import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  ApartmentForm,
  ApartmentFormValues,
} from "../../components/apartment-form";
import { Modal } from "../../components/modal";
import { useAdditionalFields } from "../../hooks/addition-fields";
import { useGetApartment } from "../../queries/get-apartment";
import {
  parseAdditionalFieldToFormValue,
  parseFormValuesToAdditionalFields,
} from "../../services/additional-fields";
import { updateApartment } from "../../services/apartment";
import { navigateBack } from "../../utils/navigate-back";
import { stringToFloat, stringToInt } from "../../utils/string-to-number";

export function ApartmentEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    throw new Error("Apartment ID is required");
  }

  const { data: apartment, isLoading: isLoadingApartment } =
    useGetApartment(id);

  const { data: additionalFields } = useAdditionalFields("apartment");
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [formValues, setFormValues] = useState<ApartmentFormValues>({
    index: "",
    objectNumber: "",
    streetNumber: "",
    apartmentNumber: "",
    floorNumber: "",
    area: "",
    share: "",
    additionalFields: additionalFields.reduce(
      (acc, field) => ({
        ...acc,
        [field.name]: "",
      }),
      {},
    ),
  });

  const isLoading = isLoadingApartment || loading;

  useEffect(() => {
    if (apartment) {
      setFormValues({
        index: apartment.index.toString(),
        objectNumber: apartment.objectNumber ?? "",
        streetNumber: apartment?.streetNumber ?? "",
        apartmentNumber: apartment?.apartmentNumber ?? "",
        floorNumber: apartment?.floorNumber ?? "",
        area: apartment?.area ? apartment.area.toString() : "",
        share: apartment?.share ? apartment.share.toString() : "",
        additionalFields: additionalFields.reduce(
          (acc, field) => ({
            ...acc,
            [field.name]: parseAdditionalFieldToFormValue(
              apartment?.additionalData?.[field.name],
            ),
          }),
          {},
        ),
      });
    }
  }, [apartment, additionalFields]);

  const closeModal = () => {
    navigateBack(navigate, "/admin/register/apartment");
  };

  return (
    <Modal open onClose={() => closeModal()}>
      <ApartmentForm
        hasError={hasError}
        loading={isLoading}
        values={formValues}
        onCancel={() => closeModal()}
        onChange={setFormValues}
        onSubmit={() => {
          (async () => {
            setLoading(true);
            try {
              await updateApartment(id, {
                index: stringToInt(formValues.index),
                objectNumber:
                  formValues.objectNumber.trim() !== ""
                    ? formValues.objectNumber
                    : null,
                streetNumber:
                  formValues.streetNumber.trim() !== ""
                    ? formValues.streetNumber
                    : null,
                apartmentNumber:
                  formValues.apartmentNumber.trim() !== ""
                    ? formValues.apartmentNumber
                    : null,
                floorNumber:
                  formValues.floorNumber.trim() !== ""
                    ? formValues.floorNumber
                    : null,
                area:
                  formValues.area.trim() !== ""
                    ? stringToFloat(formValues.area)
                    : null,
                share:
                  formValues.share.trim() !== ""
                    ? stringToFloat(formValues.share)
                    : null,
                ...(formValues.additionalFields && {
                  additionalData: parseFormValuesToAdditionalFields(
                    formValues.additionalFields,
                    additionalFields,
                    true,
                  ),
                }),
              });
              Promise.all([
                queryClient.invalidateQueries({
                  queryKey: ["apartment", id],
                }),
                queryClient.invalidateQueries({
                  queryKey: ["apartments"],
                }),
              ]);
              closeModal();
            } catch (error: unknown) {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
